/**
 * Zod schemas for the Phase 1 workflow API surface (Section 3 of the
 * design doc). These are the on-the-wire shapes — the TS types in
 * `./types.ts` are aligned to them.
 *
 * Validation pipeline:
 *  1. discriminatedUnion / object schemas handle structural validation
 *  2. ZWorkflowBody.superRefine handles cross-row checks:
 *       - step keys unique
 *       - transition fromStepKey / toStepKey resolve to a step or trigger
 *       - graph is a DAG (no cycles) — Phase 1 lints this on save (L4)
 *  3. RBAC (ADMIN_PUBLISHED requires admin role) is enforced in the
 *     controller, NOT here — Zod doesn't have the principal in scope.
 */

import { z } from 'zod';
import { WORKFLOW_EVENT_TYPES } from './events.js';
import type { ConditionSpec } from './conditions.js';
import { EMAIL_RECIPIENTS, PHONE_RECIPIENTS } from './recipients.js';

// ---------------------------------------------------------------------------
// Condition AST — recursive schema via z.lazy
// ---------------------------------------------------------------------------

const ConditionLiteralSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);

/**
 * Self-referencing schema. `z.ZodType<ConditionSpec>` annotation gives us
 * proper inference at the call site while letting us use `z.lazy` for
 * recursion. The runtime shape mirrors `ConditionSpec` exactly.
 */
export const ZCondition: z.ZodType<ConditionSpec> = z.lazy(() =>
  z.discriminatedUnion('op', [
    z.object({ op: z.literal('and'), conditions: z.array(ZCondition).min(1).max(32) }),
    z.object({ op: z.literal('or'), conditions: z.array(ZCondition).min(1).max(32) }),
    z.object({ op: z.literal('not'), condition: ZCondition }),
    z.object({ op: z.literal('eq'), path: z.string().min(1).max(200), value: ConditionLiteralSchema }),
    z.object({ op: z.literal('neq'), path: z.string().min(1).max(200), value: ConditionLiteralSchema }),
    z.object({ op: z.literal('gt'), path: z.string().min(1).max(200), value: z.number() }),
    z.object({ op: z.literal('gte'), path: z.string().min(1).max(200), value: z.number() }),
    z.object({ op: z.literal('lt'), path: z.string().min(1).max(200), value: z.number() }),
    z.object({ op: z.literal('lte'), path: z.string().min(1).max(200), value: z.number() }),
    z.object({ op: z.literal('exists'), path: z.string().min(1).max(200) }),
    z.object({ op: z.literal('not_exists'), path: z.string().min(1).max(200) }),
  ]),
);

// ---------------------------------------------------------------------------
// Actions (Section 3)
// ---------------------------------------------------------------------------

export const ZAction = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('email'),
    templateKey: z.string().min(1).max(120),
    // Tuple cast: z.enum demands a [string, ...string[]] but readonly tuples
    // satisfy that shape at runtime; the cast carries no runtime cost.
    to: z.enum(EMAIL_RECIPIENTS as unknown as [string, ...string[]]),
    customTo: z.string().email().optional(),
  }),
  z.object({
    kind: z.literal('whatsapp'),
    templateKey: z.string().min(1).max(120),
    to: z.enum(PHONE_RECIPIENTS as unknown as [string, ...string[]]),
    customTo: z.string().optional(),
  }),
  z.object({
    kind: z.literal('sms'),
    templateKey: z.string().min(1).max(120),
    to: z.enum(PHONE_RECIPIENTS as unknown as [string, ...string[]]),
    customTo: z.string().optional(),
  }),
  z.object({
    kind: z.literal('internal_task'),
    title: z.string().min(1).max(200),
    assignee: z.enum(['owner', 'mlo', 'processor']),
  }),
]);

// ---------------------------------------------------------------------------
// Triggers — workflow-level, NOT per-step (C3)
// ---------------------------------------------------------------------------

export const ZTriggerTime = z.object({
  type: z.literal('time'),
  mode: z.enum(['cron', 'relative_offset']),
  cron: z.string().min(1).max(120).optional(),
  offsetDays: z.number().int().min(-3650).max(3650).optional(),
  anchorField: z.string().min(1).max(120).optional(),
  timezone: z.string().min(1).max(80).default('America/Chicago'),
  entryStepKey: z.string().optional(),
});

export const ZTriggerEvent = z.object({
  type: z.literal('event'),
  event: z.enum(WORKFLOW_EVENT_TYPES),
  matchValue: z.string().max(200).optional(),
  entryStepKey: z.string().optional(),
});

export const ZTriggerCondition = z.object({
  type: z.literal('condition'),
  predicate: ZCondition,
  entryStepKey: z.string().optional(),
});

export const ZTrigger = z.discriminatedUnion('type', [
  ZTriggerTime,
  ZTriggerEvent,
  ZTriggerCondition,
]);

// ---------------------------------------------------------------------------
// Steps + Transitions
// ---------------------------------------------------------------------------

/**
 * Step key constraint (H1 append-only after publish): uppercase letters,
 * digits, underscore, hyphen, 2-32 chars. Matches "WF1-S1" through
 * "WF1-S4" so the migration backfill writes the exact same keys.
 */
export const STEP_KEY_REGEX = /^[A-Z0-9_-]{2,32}$/;

export const ZStep = z.object({
  key: z.string().regex(STEP_KEY_REGEX, {
    message: 'Step keys must be 2-32 chars, uppercase letters / digits / underscore / hyphen',
  }),
  name: z.string().min(1).max(80),
  category: z.enum(['NOTIFY', 'WAIT', 'BRANCH', 'TASK', 'TERMINAL']),
  guardJson: ZCondition.optional(),
  actions: z.array(ZAction).max(8).default([]),
  cooldownHours: z.number().int().min(0).max(720).default(0),
  isTerminal: z.boolean().default(false),
});

export const ZTransition = z.object({
  /** null = entry edge from a trigger. */
  fromStepKey: z.string().nullable(),
  toStepKey: z.string(),
  label: z.string().max(60).optional(),
  guardJson: ZCondition.optional(),
});

// ---------------------------------------------------------------------------
// Workflow body — POST /workflows + PATCH /workflows/:id structural part
// ---------------------------------------------------------------------------

export const ZWorkflowBody = z
  .object({
    name: z.string().min(1).max(120),
    description: z.string().max(2000).optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).default([]),
    visibility: z.enum(['PRIVATE', 'ORG_PUBLISHED', 'ADMIN_PUBLISHED']).default('PRIVATE'),
    /** Defaults TRUE per Phase 1 product decision (locked in by user). */
    attributionVisible: z.boolean().default(true),
    appliesToProducts: z.array(z.string().min(1).max(40)).max(50).default([]),
    appliesToStates: z.array(z.string().min(1).max(80)).max(50).default([]),
    triggers: z.array(ZTrigger).min(1).max(20),
    steps: z.array(ZStep).min(1).max(200),
    transitions: z.array(ZTransition).max(400).default([]),
    changeNote: z.string().max(500).optional(),
  })
  .superRefine((body, ctx) => {
    // ---- 1. step keys unique ---------------------------------------------
    const stepKeySet = new Set<string>();
    body.steps.forEach((step, idx) => {
      if (stepKeySet.has(step.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['steps', idx, 'key'],
          message: `Duplicate step key "${step.key}"`,
        });
      } else {
        stepKeySet.add(step.key);
      }
    });

    // ---- 2. trigger entryStepKey + transition stepKeys resolve -----------
    body.triggers.forEach((trigger, idx) => {
      if (trigger.entryStepKey && !stepKeySet.has(trigger.entryStepKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['triggers', idx, 'entryStepKey'],
          message: `entryStepKey "${trigger.entryStepKey}" does not match any step`,
        });
      }
    });

    body.transitions.forEach((t, idx) => {
      if (t.fromStepKey !== null && !stepKeySet.has(t.fromStepKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['transitions', idx, 'fromStepKey'],
          message: `fromStepKey "${t.fromStepKey}" does not match any step`,
        });
      }
      if (!stepKeySet.has(t.toStepKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['transitions', idx, 'toStepKey'],
          message: `toStepKey "${t.toStepKey}" does not match any step`,
        });
      }
    });

    // ---- 3. at least one entry edge --------------------------------------
    // Any trigger is an entry edge — when entryStepKey is omitted the
    // runtime enters the workflow at the first step. So we accept either an
    // explicit null-from transition (manual entry edge wired in the builder)
    // OR any trigger at all.
    const hasEntryEdge =
      body.transitions.some((t) => t.fromStepKey === null) || body.triggers.length > 0;
    if (!hasEntryEdge && body.steps.length > 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transitions'],
        message:
          'Workflow has no entry edge — add a trigger, or wire a transition from null (entry) to your first step.',
      });
    }

    // ---- 4. DAG check (no cycles) (L4) -----------------------------------
    // Build adjacency from step keys; ignore null-from transitions (entry edges).
    const adj = new Map<string, string[]>();
    for (const step of body.steps) adj.set(step.key, []);
    for (const t of body.transitions) {
      if (t.fromStepKey === null) continue;
      if (!stepKeySet.has(t.fromStepKey) || !stepKeySet.has(t.toStepKey)) continue;
      adj.get(t.fromStepKey)!.push(t.toStepKey);
    }
    const WHITE = 0,
      GREY = 1,
      BLACK = 2;
    const color = new Map<string, number>();
    for (const key of stepKeySet) color.set(key, WHITE);

    let cycleNode: string | null = null;
    const visit = (node: string): boolean => {
      color.set(node, GREY);
      for (const next of adj.get(node) ?? []) {
        const c = color.get(next);
        if (c === GREY) {
          cycleNode = next;
          return true;
        }
        if (c === WHITE && visit(next)) return true;
      }
      color.set(node, BLACK);
      return false;
    };
    for (const key of stepKeySet) {
      if (color.get(key) === WHITE && visit(key)) break;
    }
    if (cycleNode !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['transitions'],
        message: `Workflow graph has a cycle through step "${cycleNode}". Phase 1 requires a DAG.`,
      });
    }
  });

export type WorkflowBody = z.infer<typeof ZWorkflowBody>;

// ---------------------------------------------------------------------------
// Copy (Section 3): POST /workflows/:id/copy
// ---------------------------------------------------------------------------

export const ZCopyBody = z.object({
  name: z.string().min(1).max(120).optional(),
  startDisabled: z.boolean().default(true),
});
export type CopyBody = z.infer<typeof ZCopyBody>;

// ---------------------------------------------------------------------------
// List query (Section 3): GET /workflows
// ---------------------------------------------------------------------------

export const ZListQuery = z.object({
  scope: z
    .enum(['mine', 'admin-published', 'peer-published', 'drafts', 'archived'])
    .default('mine'),
  q: z.string().max(120).optional(),
  tag: z.string().max(40).optional(),
  enabled: z.coerce.boolean().optional(),
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  sort: z
    .enum(['updatedAt:desc', 'updatedAt:asc', 'name:asc', 'name:desc'])
    .default('updatedAt:desc'),
});
export type ListQuery = z.infer<typeof ZListQuery>;

// ---------------------------------------------------------------------------
// Unpublish body — Phase 1 product decision: admin "force-cancel everything"
// is IN scope. UI exposes the toggle on the unpublish popover.
// ---------------------------------------------------------------------------

export const ZUnpublishBody = z.object({
  /**
   * When true, every in-flight WorkflowInstance for this workflow is
   * cancelled with a per-instance audit row. Admin-only at the controller.
   * When false (default), unpublish is a soft retire: no new starts,
   * in-flight instances drain to completion.
   */
  forceCancel: z.boolean().default(false),
  reason: z.string().max(500).optional(),
});
export type UnpublishBody = z.infer<typeof ZUnpublishBody>;

// ---------------------------------------------------------------------------
// Test-run / dry-run bodies
// ---------------------------------------------------------------------------

export const ZTestRunBody = z.object({
  loanId: z.string().min(1),
  sendNotifications: z.boolean().default(false),
  maxSteps: z.number().int().min(1).max(50).default(10),
});
export type TestRunBody = z.infer<typeof ZTestRunBody>;

export const ZDryRunBody = z.object({
  loanId: z.string().min(1),
});
export type DryRunBody = z.infer<typeof ZDryRunBody>;
